import { supabase } from '../lib/supabase';

// Import calendar service for milestone sync
import { calendarService } from './calendarService';
import { isDemoMode, getDemoOrgId, getAuthOrgId } from '../utils/demoHelper';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Upload project photo to Supabase Storage
const uploadProjectPhoto = async (file) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
    const filePath = `projects/${fileName}`;

    const { data, error } = await supabase?.storage
      ?.from('project-photos')
      ?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    // Get public URL
    const { data: { publicUrl } } = supabase?.storage
      ?.from('project-photos')
      ?.getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading project photo:', error);
    throw error;
  }
};

// Delete project photo from Supabase Storage
const deleteProjectPhoto = async (imageUrl) => {
  try {
    if (!imageUrl) return;

    // Extract file path from URL
    const urlParts = imageUrl?.split('/project-photos/');
    if (urlParts?.length < 2) return;

    const filePath = urlParts?.[1];

    const { error } = await supabase?.storage
      ?.from('project-photos')
      ?.remove([filePath]);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting project photo:', error);
    // Don't throw - allow operation to continue even if delete fails
  }
};

export const projectService = {
  // Get all projects
  async getAll() {
    try {
      let query = supabase?.from('projects')?.select('*')?.order('created_at', { ascending: false });

      // Demo mode: use stored demo org_id directly (no real Supabase session)
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) {
          query = query?.eq('organization_id', demoOrgId);
        }
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        let orgId = getAuthOrgId();
        if (orgId) {
          query = query?.eq('organization_id', orgId);
        }
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  // Get assigned contractors for a project
  async getAssignedContractors(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('project_assignments')
        ?.select('*, contractors(*)')
        ?.eq('project_id', projectId)
        ?.order('assigned_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching assigned contractors:', error);
      throw error;
    }
  },

  // Assign contractor to project
  async assignContractor(projectId, contractorId, assignmentData = {}) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      const user = session?.user;

      // Use cached org_id from AuthContext
      const organizationId = getAuthOrgId();
      if (!organizationId) {
        throw new Error('Unable to determine user organization');
      }

      const snakeData = toSnakeCase({
        projectId,
        contractorId,
        organizationId,
        ...assignmentData,
        createdBy: user?.id
      });

      const { data, error } = await supabase
        ?.from('project_assignments')?.insert(snakeData)?.select('*, contractors(*)')
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error assigning contractor:', error);
      throw error;
    }
  },

  // Unassign contractor from project
  async unassignContractor(assignmentId) {
    try {
      const { error } = await supabase
        ?.from('project_assignments')?.delete()?.eq('id', assignmentId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return true;
    } catch (error) {
      console.error('Error unassigning contractor:', error);
      throw error;
    }
  },

  // Get assigned team members for a project
  async getAssignedTeamMembers(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('project_team_assignments')
        ?.select('*, user_profiles!project_team_assignments_team_member_id_fkey(*)')
        ?.eq('project_id', projectId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching assigned team members:', error);
      throw error;
    }
  },

  // Assign team member to project
  async assignTeamMember(projectId, teamMemberId, assignmentData = {}) {
    try {
      let orgId = null;
      let userId = null;

      if (isDemoMode()) {
        orgId = getDemoOrgId();
      } else {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (!session?.user) throw new Error('User not authenticated');
        userId = session?.user?.id;

        // Use cached org_id from AuthContext
        orgId = getAuthOrgId();
        if (!orgId) {
          throw new Error('Unable to determine user organization');
        }

        // Guard: verify the team member exists in user_profiles (not a pending invite)
        const { data: memberProfile } = await supabase
          ?.from('user_profiles')?.select('id')?.eq('id', teamMemberId)?.maybeSingle();
        if (!memberProfile) {
          throw new Error('This team member has not yet accepted their invitation and cannot be assigned to a project.');
        }
      }

      const snakeData = toSnakeCase({
        projectId,
        teamMemberId,
        organizationId: orgId,
        ...assignmentData,
        ...(userId ? { createdBy: userId } : {})
      });

      const { data, error } = await supabase
        ?.from('project_team_assignments')?.insert(snakeData)?.select('*, user_profiles!project_team_assignments_team_member_id_fkey(*)')
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === '23505') {
          // Duplicate assignment — fetch and return the existing record
          const { data: existing, error: fetchError } = await supabase
            ?.from('project_team_assignments')
            ?.select('*, user_profiles!project_team_assignments_team_member_id_fkey(*)')
            ?.eq('project_id', snakeData?.project_id)
            ?.eq('team_member_id', snakeData?.team_member_id)
            ?.single();
          if (fetchError) throw new Error(fetchError.message);
          return toCamelCase(existing);
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error assigning team member:', error);
      throw error;
    }
  },

  // Unassign team member from project
  async unassignTeamMember(assignmentId) {
    try {
      const { error } = await supabase
        ?.from('project_team_assignments')?.delete()?.eq('id', assignmentId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return true;
    } catch (error) {
      console.error('Error unassigning team member:', error);
      throw error;
    }
  },

  // Get project permits
  async getProjectPermits(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('permits')?.select('*')?.eq('project_id', projectId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching project permits:', error);
      throw error;
    }
  },

  // Get project by ID
  async getById(id) {
    try {
      let query = supabase?.from('projects')?.select('*')?.eq('id', id)?.maybeSingle();

      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) query = query?.eq('organization_id', demoOrgId);
      } else {
        // Use cached org_id from AuthContext
        let orgId = getAuthOrgId();
        if (orgId) {
          query = query?.eq('organization_id', orgId);
        }
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  },

  // Create new project
  async create(projectData) {
    try {
      if (isDemoMode()) throw new Error('Project creation is not available in demo mode.');

      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      const user = session?.user;

      // Use cached org_id from AuthContext
      const organizationId = getAuthOrgId();
      if (!organizationId) {
        throw new Error('Unable to determine user organization');
      }

      // Handle image upload if provided
      let imageUrl = null;
      if (projectData?.imageFile) {
        imageUrl = await uploadProjectPhoto(projectData?.imageFile);
      }

      const snakeData = toSnakeCase({
        ...projectData,
        imageUrl,
        imageFile: undefined, // Remove file object
        createdBy: user?.id,
        organizationId
      });

      const { data, error } = await supabase
        ?.from('projects')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        // Clean up uploaded image if project creation fails
        if (imageUrl) await deleteProjectPhoto(imageUrl);
        
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  // Update project
  async update(id, projectData) {
    try {
      // Use cached org_id from AuthContext
      const organizationId = isDemoMode() ? getDemoOrgId() : getAuthOrgId();

      // Handle image upload if new image provided
      let imageUrl = projectData?.imageUrl;
      if (projectData?.imageFile) {
        // Delete old image if exists
        if (projectData?.oldImageUrl) {
          await deleteProjectPhoto(projectData?.oldImageUrl);
        }
        // Upload new image
        imageUrl = await uploadProjectPhoto(projectData?.imageFile);
      }

      const snakeData = toSnakeCase({
        ...projectData,
        imageUrl,
        imageFile: undefined, // Remove file object
        oldImageUrl: undefined, // Remove old URL reference
        ...(organizationId ? { organizationId } : {})
      });

      const { data, error } = await supabase
        ?.from('projects')?.update(snakeData)?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        // Clean up uploaded image if update fails
        if (projectData?.imageFile && imageUrl) {
          await deleteProjectPhoto(imageUrl);
        }
        
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      const updatedProject = toCamelCase(data);

      // Sync phase and due date milestones to calendar (non-blocking)
      if (projectData?.phase || projectData?.dueDate !== undefined) {
        calendarService?.syncProjectMilestones(id, {
          phase: updatedProject?.phase,
          customerName: updatedProject?.customerName,
          address: updatedProject?.address,
          dueDate: updatedProject?.dueDate || null
        });
      }

      return updatedProject;
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  // Delete project
  async delete(id) {
    try {
      // Get project to retrieve image URL
      const { data: project } = await supabase
        ?.from('projects')?.select('image_url')?.eq('id', id)
        ?.single();

      // Delete project
      const { error } = await supabase
        ?.from('projects')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      // Delete associated image
      if (project?.image_url) {
        await deleteProjectPhoto(project?.image_url);
      }

      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  }
};